import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { ENV } from '../../../config/env';
import { BrandKitState } from '../BrandKitCreator';

interface Props {
  brandKit: BrandKitState;
  updateBrandKit: (updates: Partial<BrandKitState>) => void;
  onNext: () => void;
  onBack: () => void;
}

const EmailSignatureTab: React.FC<Props> = ({ brandKit, updateBrandKit, onNext, onBack }) => {
  const { isAuthenticated, getToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('modern');
  const [signatureHtml, setSignatureHtml] = useState('');
  
  // Form fields - pre-filled from brand kit
  const [fullName, setFullName] = useState(brandKit.fullName || '');
  const [title, setTitle] = useState(brandKit.title || '');
  const [company, setCompany] = useState(brandKit.businessName || '');
  const [email, setEmail] = useState(brandKit.email || '');
  const [phone, setPhone] = useState(brandKit.phone || '');
  const [website, setWebsite] = useState(brandKit.website || '');
  const [address, setAddress] = useState(brandKit.address || '');
  const [tagline, setTagline] = useState(brandKit.tagline || '');
  const [socials, setSocials] = useState<Record<string, string>>(brandKit.socials || {});
  const [primaryColor, setPrimaryColor] = useState(brandKit.primaryColor || '#0ea5e9');
  const [accentColor, setAccentColor] = useState(brandKit.accentColor || '#111827');

  const canGenerate = useMemo(() => {
    return !!fullName && !!email;
  }, [fullName, email]);

  // Update company when brand kit changes
  useEffect(() => {
    if (brandKit.businessName && !company) {
      setCompany(brandKit.businessName);
    }
    if (brandKit.tagline && !tagline) {
      setTagline(brandKit.tagline);
    }
  }, [brandKit.businessName, brandKit.tagline, company, tagline]);

  const handleSocialChange = (platform: string, value: string) => {
    setSocials((prev) => ({ ...prev, [platform]: value }));
  };

  const submit = async () => {
    if (!isAuthenticated) {
      toast.error('Please sign in to use the Email Signature Creator');
      return;
    }
    if (!canGenerate) {
      toast.error('Please provide at least your name and email');
      return;
    }

    setLoading(true);
    try {
      const userId = await getToken();
      if (!userId) {
        toast.error('Unable to identify user. Please re-login.');
        setLoading(false);
        return;
      }

      const payload = {
        userId,
        fullName,
        title,
        company,
        email,
        phone,
        website,
        address,
        tagline,
        socials,
        primaryColor,
        accentColor,
        template: selectedTemplate,
        logoUrl: brandKit.logoDataUrl || brandKit.logoUrl,
      };

      const response = await fetch(`${ENV.API_URL}/api/tools/email-signature`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error('Failed to generate signature');
      }

      const data = await response.json();
      setSignatureHtml(data.html);
      
      updateBrandKit({
        fullName,
        title,
        company,
        email,
        phone,
        website,
        address,
        socials,
        primaryColor,
        accentColor,
        signatureHtml: data.html,
      });
      
      toast.success('Email signature generated!');
    } catch (error) {
      console.error('Email signature generation error:', error);
      toast.error('Failed to generate signature. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      const blob = new Blob([signatureHtml], { type: 'text/html' });
      const clipboardItem = new ClipboardItem({ 'text/html': blob });
      await navigator.clipboard.write([clipboardItem]);
      toast.success('Signature copied! Paste it into your email settings.');
    } catch (error) {
      // Fallback for browsers that don't support clipboard API
      const textarea = document.createElement('textarea');
      textarea.value = signatureHtml;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      toast.success('Signature HTML copied!');
    }
  };

  const handleContinue = () => {
    if (!signatureHtml) {
      toast.error('Please generate your email signature first');
      return;
    }
    onNext();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Step 3: Create Email Signature</h2>
          <p className="text-gray-600">
            Professional signature for <span className="font-semibold text-orange-600">{company}</span>
          </p>
        </div>
        <button
          onClick={onBack}
          className="px-4 py-2 text-gray-600 hover:text-gray-900 transition"
        >
          ← Back
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form Column */}
        <div className="space-y-6">
          {/* Template Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Template Style
            </label>
            <select
              value={selectedTemplate}
              onChange={(e) => setSelectedTemplate(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            >
              <option value="modern">Modern (Gradient)</option>
              <option value="minimalist">Minimalist (Clean)</option>
              <option value="corporate">Corporate (Traditional)</option>
              <option value="creative">Creative (Bold)</option>
              <option value="tech">Tech (Sleek)</option>
            </select>
          </div>

          {/* Personal Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="John Smith"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="CEO"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Company</label>
              <input
                type="text"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-orange-50"
                placeholder="Company Name"
                readOnly
              />
            </div>
            
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="john@company.com"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="+1 (555) 123-4567"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Website</label>
              <input
                type="url"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="https://company.com"
              />
            </div>
            
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
              <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="123 Main St, City, State 12345"
              />
            </div>
            
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Tagline</label>
              <input
                type="text"
                value={tagline}
                onChange={(e) => setTagline(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-orange-50"
                placeholder="Your business tagline"
                readOnly
              />
            </div>
          </div>

          {/* Social Links */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Social Links</label>
            <div className="space-y-2">
              {['linkedin', 'twitter', 'github', 'facebook', 'instagram'].map(platform => (
                <input
                  key={platform}
                  type="url"
                  value={socials[platform] || ''}
                  onChange={(e) => handleSocialChange(platform, e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder={`${platform.charAt(0).toUpperCase() + platform.slice(1)} URL`}
                />
              ))}
            </div>
          </div>

          {/* Colors */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Primary Color</label>
              <input
                type="color"
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
                className="w-full h-12 border border-gray-300 rounded-lg cursor-pointer"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Accent Color</label>
              <input
                type="color"
                value={accentColor}
                onChange={(e) => setAccentColor(e.target.value)}
                className="w-full h-12 border border-gray-300 rounded-lg cursor-pointer"
              />
            </div>
          </div>

          {/* Generate Button */}
          <button
            onClick={submit}
            disabled={loading || !canGenerate}
            className="w-full px-6 py-3 bg-orange-600 text-white font-semibold rounded-lg hover:bg-orange-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Generating...' : 'Generate Signature'}
          </button>
        </div>

        {/* Preview Column */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Live Preview</label>
          <div className="border-2 border-gray-200 rounded-lg p-6 bg-white min-h-[400px]">
            {signatureHtml ? (
              <>
                <div dangerouslySetInnerHTML={{ __html: signatureHtml }} />
                <div className="mt-6 pt-6 border-t border-gray-200 flex gap-2">
                  <button
                    onClick={copyToClipboard}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                  >
                    Copy HTML
                  </button>
                  <button
                    onClick={submit}
                    className="px-4 py-2 text-gray-600 hover:text-gray-900 transition"
                  >
                    Regenerate
                  </button>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400">
                <div className="text-center">
                  <svg className="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <p>Fill in your details and click Generate to see your signature</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Continue Button */}
      {signatureHtml && (
        <button
          onClick={handleContinue}
          className="w-full px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition flex items-center justify-center gap-2"
        >
          Continue to Download Kit
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}
    </div>
  );
};

export default EmailSignatureTab;
